import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useStore } from '../store/useStore';
import { ContractWithDetails, ContractStatus, ContractField } from '../types/contracts';
import {
  FileText, Plus, Search, ChevronRight, ChevronLeft,
  Clock, CheckCircle2, Send, Eye, XCircle, RefreshCw,
  FileSignature, Loader2, BookOpen, Trash2, AlertCircle, Star,
  FolderOpen, Copy, Upload, Folder, FolderPlus, Check, X, Edit2
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { PREBUILT_TEMPLATES, getCustomTemplates, saveCustomTemplate, deleteCustomTemplate, ContractTemplate } from '../data/contractTemplates';
import { Modal } from '../components/Modal';
import { parseTemplateFile } from '../lib/templateParser';

const STATUS_CONFIG: Record<ContractStatus, { label: string; color: string; icon: any }> = {
  draft:     { label: 'Draft',     color: 'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)]', icon: FileText },
  sent:      { label: 'Sent',      color: 'bg-[var(--t-info-dim)] text-[var(--t-info)]',             icon: Send },
  viewed:    { label: 'Viewed',    color: 'bg-blue-500/10 text-blue-400',                             icon: Eye },
  countered: { label: 'Countered', color: 'bg-[var(--t-warning-dim)] text-[var(--t-warning)]',       icon: RefreshCw },
  accepted:  { label: 'Accepted',  color: 'bg-emerald-500/10 text-emerald-400',                       icon: CheckCircle2 },
  executed:  { label: 'Executed',  color: 'bg-[var(--t-success-dim)] text-[var(--t-success)]',        icon: FileSignature },
  rejected:  { label: 'Rejected',  color: 'bg-[var(--t-error)]/10 text-[var(--t-error)]',             icon: XCircle },
};

const FILTER_TABS: Array<{ label: string; value: ContractStatus | 'all' }> = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Viewed', value: 'viewed' },
  { label: 'Countered', value: 'countered' },
  { label: 'Accepted', value: 'accepted' },
  { label: 'Executed', value: 'executed' },
  { label: 'Rejected', value: 'rejected' },
];

const DELETABLE_STATUSES: ContractStatus[] = ['draft', 'rejected'];
const PAGE_SIZE = 20;

type PageView = 'contracts' | 'templates';

export default function Contracts() {
  const navigate = useNavigate();
  const { currentUser, leads } = useStore();

  const [contracts, setContracts] = useState<ContractWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ContractStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [pageView, setPageView] = useState<PageView>('contracts');
  const [page, setPage] = useState(1);

  // Delete state
  const [deletingContractId, setDeletingContractId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Templates
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [newTemplateCat, setNewTemplateCat] = useState('Custom');
  const [newTemplateFile, setNewTemplateFile] = useState<File | null>(null);
  const [newTemplateText, setNewTemplateText] = useState('');

  // Folders and Category Management
  const [folders, setFolders] = useState<string[]>([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [isAddingFolder, setIsAddingFolder] = useState(false);
  const [editingFolder, setEditingFolder] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');

  // Default Template Selection
  const [defaultTemplateId, setDefaultTemplateId] = useState<string | null>(null);

  // Extracted Fields for upload customization
  const [extractedFields, setExtractedFields] = useState<ContractField[]>([]);

  // Load folders and default template on mount
  useEffect(() => {
    const saved = localStorage.getItem('contract_folders');
    if (saved) {
      setFolders(JSON.parse(saved));
    } else {
      const defaultFolders = ['Acquisitions', 'Dispositions', 'Rentals', 'Management', 'Custom'];
      setFolders(defaultFolders);
      localStorage.setItem('contract_folders', JSON.stringify(defaultFolders));
    }

    setDefaultTemplateId(localStorage.getItem('default_contract_template_id'));
  }, []);

  // Extract fields when newTemplateText changes in upload modal
  useEffect(() => {
    if (showUploadModal) {
      const fields = extractFieldsFromTerms(newTemplateText);
      setExtractedFields(prev => {
        return fields.map(f => {
          const existing = prev.find(p => p.id === f.id);
          return existing ? existing : f;
        });
      });
    } else {
      setExtractedFields([]);
    }
  }, [newTemplateText, showUploadModal]);

  const handleCreateFolder = () => {
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      toast.error('Folder name cannot be empty');
      return;
    }
    if (folders.includes(trimmed)) {
      toast.error('Folder already exists');
      return;
    }
    const newFolders = [...folders, trimmed];
    setFolders(newFolders);
    localStorage.setItem('contract_folders', JSON.stringify(newFolders));
    setNewFolderName('');
    setIsAddingFolder(false);
    toast.success(`Folder "${trimmed}" created`);
  };

  const handleRenameFolder = async (oldName: string) => {
    const trimmed = editingFolderName.trim();
    if (!trimmed) {
      toast.error('Folder name cannot be empty');
      return;
    }
    if (folders.includes(trimmed) && trimmed !== oldName) {
      toast.error('Folder name already exists');
      return;
    }

    const newFolders = folders.map(f => f === oldName ? trimmed : f);
    setFolders(newFolders);
    localStorage.setItem('contract_folders', JSON.stringify(newFolders));

    // Update custom templates category
    const custom = getCustomTemplates();
    custom.forEach(t => {
      if (t.category === oldName) {
        t.category = trimmed;
        saveCustomTemplate(t);
      }
    });

    if (isSupabaseConfigured && supabase && currentUser) {
      try {
        await supabase
          .from('contract_templates')
          .update({ category: trimmed })
          .eq('user_id', currentUser.id)
          .eq('category', oldName);
      } catch (err: any) {
        console.error('Failed to sync renamed folder to Supabase:', err);
      }
    }

    setEditingFolder(null);
    setEditingFolderName('');
    toast.success(`Renamed folder to "${trimmed}"`);
    await fetchTemplates();
  };

  const handleDeleteFolder = async (folderName: string) => {
    const newFolders = folders.filter(f => f !== folderName);
    setFolders(newFolders);
    localStorage.setItem('contract_folders', JSON.stringify(newFolders));

    // Delete custom templates in this folder
    const custom = getCustomTemplates();
    const toDelete = custom.filter(t => t.category === folderName);
    
    for (const t of toDelete) {
      if (isSupabaseConfigured && supabase && currentUser) {
        try {
          await supabase
            .from('contract_templates')
            .delete()
            .eq('id', t.id)
            .eq('user_id', currentUser.id);
        } catch (err) {
          console.error(err);
        }
      }
      deleteCustomTemplate(t.id);
    }

    toast.success(`Deleted folder "${folderName}" and its custom templates`);
    await fetchTemplates();
  };

  const handleDuplicateTemplate = async (template: ContractTemplate) => {
    if (!currentUser) {
      toast.error('Not authenticated');
      return;
    }
    try {
      const newId = `custom-${uuidv4()}`;
      const duplicated: ContractTemplate = {
        id: newId,
        name: `Copy of ${template.name}`,
        category: template.category,
        description: template.description || 'Duplicated template',
        icon: template.icon || '📄',
        content: JSON.parse(JSON.stringify(template.content))
      };

      if (isSupabaseConfigured && supabase) {
        const { error } = await supabase
          .from('contract_templates')
          .insert({
            id: duplicated.id,
            user_id: currentUser.id,
            name: duplicated.name,
            category: duplicated.category,
            description: duplicated.description,
            icon: duplicated.icon,
            content: duplicated.content
          });
        if (error) throw error;
      } else {
        saveCustomTemplate(duplicated);
      }
      toast.success(`Duplicated "${template.name}"`);
      await fetchTemplates();
    } catch (err: any) {
      toast.error(`Duplication failed: ${err.message}`);
    }
  };

  const handleSetDefaultTemplate = (id: string) => {
    const current = localStorage.getItem('default_contract_template_id');
    if (current === id) {
      localStorage.removeItem('default_contract_template_id');
      setDefaultTemplateId(null);
      toast.success('Default template cleared');
    } else {
      localStorage.setItem('default_contract_template_id', id);
      setDefaultTemplateId(id);
      toast.success('Template set as default');
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [currentUser?.id, pageView]);

  const fetchTemplates = async () => {
    let custom: ContractTemplate[] = [];
    if (isSupabaseConfigured && supabase && currentUser) {
      try {
        const { data, error } = await supabase
          .from('contract_templates')
          .select('*')
          .eq('user_id', currentUser.id);
        
        if (error) throw error;
        if (data) {
          custom = data.map((t: any) => ({
            id: t.id,
            name: t.name,
            category: t.category,
            description: t.description || '',
            icon: t.icon || '📄',
            content: typeof t.content === 'string' ? JSON.parse(t.content) : t.content
          }));
        }
      } catch (err: any) {
        console.error('Failed to fetch custom templates from Supabase:', err);
        custom = getCustomTemplates();
      }
    } else {
      custom = getCustomTemplates();
    }
    setTemplates([...PREBUILT_TEMPLATES, ...custom]);
  };

  const fetchContracts = async () => {
    if (!isSupabaseConfigured || !supabase) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      // Fetch contracts + signatures in parallel (skip versions for speed)
      const [contractsResult, signaturesResult] = await Promise.all([
        supabase.from('contracts').select('*').order('updated_at', { ascending: false }),
        supabase.from('contract_signatures').select('id, contract_id, signer_type, signed_at'),
      ]);

      if (contractsResult.error) throw contractsResult.error;

      const enriched: ContractWithDetails[] = (contractsResult.data || []).map((c: any) => ({
        ...c,
        signatures: (signaturesResult.data || []).filter((s: any) => s.contract_id === c.id),
        versions: [],
        activity_log: c.activity_log || [],
      }));

      setContracts(enriched);
    } catch (err: any) {
      toast.error(`Failed to load contracts: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewContract = async (template?: ContractTemplate) => {
    if (!currentUser || !supabase) {
      toast.error('Not authenticated');
      return;
    }
    setIsCreating(true);
    try {
      let activeTemplate = template;
      if (!activeTemplate && defaultTemplateId) {
        activeTemplate = templates.find(t => t.id === defaultTemplateId);
      }

      const newId = uuidv4();
      const title = activeTemplate
        ? `${activeTemplate.name} — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
        : `New Contract — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      const activityLog = [{
        id: uuidv4(),
        action: 'created',
        actor: currentUser.name || 'Agent',
        timestamp: new Date().toISOString(),
        note: activeTemplate ? `Created from template: ${activeTemplate.name}` : 'Contract created',
      }];

      const { error: contractErr } = await supabase.from('contracts').insert({
        id: newId,
        title,
        status: 'draft',
        created_by: currentUser.id,
        activity_log: activityLog,
      });
      if (contractErr) throw contractErr;

      // If created from template, pre-create version 1 with template content
      if (activeTemplate) {
        await supabase.from('contract_versions').insert({
          contract_id: newId,
          version_number: 1,
          content: activeTemplate.content,
          change_summary: `From template: ${activeTemplate.name}`,
          created_by: currentUser.id,
        });
      }

      toast.success(activeTemplate ? `Contract created from template: ${activeTemplate.name}` : 'Contract created');
      navigate(`/contracts/${newId}`);
    } catch (err: any) {
      toast.error(`Failed to create contract: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!deletingContractId || !supabase) return;
    setIsDeleting(true);
    try {
      const { error } = await supabase.from('contracts').delete().eq('id', deletingContractId);
      if (error) throw error;
      setContracts(prev => prev.filter(c => c.id !== deletingContractId));
      toast.success('Contract deleted');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeletingContractId(null);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    if (isSupabaseConfigured && supabase && currentUser) {
      try {
        const { error } = await supabase
          .from('contract_templates')
          .delete()
          .eq('id', id)
          .eq('user_id', currentUser.id);
        if (error) throw error;
        toast.success('Template deleted');
      } catch (err: any) {
        toast.error(`Failed to delete template: ${err.message}`);
        return;
      }
    } else {
      deleteCustomTemplate(id);
      toast.success('Template deleted');
    }
    await fetchTemplates();
  };

  const extractFieldsFromTerms = (terms: string): ContractField[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const fields: ContractField[] = [];
    const seenIds = new Set<string>();
    let match;
    while ((match = regex.exec(terms)) !== null) {
      const rawName = match[1].trim();
      const id = rawName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (!seenIds.has(id)) {
        seenIds.add(id);
        let type: 'text' | 'date' | 'number' | 'currency' | 'textarea' = 'text';
        const nameLower = rawName.toLowerCase();
        if (nameLower.includes('date')) {
          type = 'date';
        } else if (nameLower.includes('price') || nameLower.includes('fee') || nameLower.includes('money') || nameLower.includes('deposit') || nameLower.includes('amount')) {
          type = 'currency';
        } else if (nameLower.includes('term') || nameLower.includes('scope') || nameLower.includes('description') || nameLower.includes('text') || nameLower.includes('clause')) {
          type = 'textarea';
        } else if (nameLower.includes('number') || nameLower.includes('days') || nameLower.includes('period') || nameLower.includes('count')) {
          type = 'number';
        }
        fields.push({
          id,
          name: rawName,
          value: '',
          visibility: 'all',
          type
        });
      }
    }
    return fields;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewTemplateFile(file);
    if (!newTemplateName) {
      const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
      setNewTemplateName(nameWithoutExt);
    }
    setUploading(true);
    try {
      const text = await parseTemplateFile(file);
      setNewTemplateText(text);
      toast.success('File parsed successfully');
    } catch (err: any) {
      toast.error(err.message);
      setNewTemplateFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (!newTemplateText.trim()) {
      toast.error('No template terms text found. Please upload a file or paste terms.');
      return;
    }
    if (!currentUser) {
      toast.error('Not authenticated');
      return;
    }

    setUploading(true);
    try {
      // Use the user-customized extractedFields (type/visibility per field)
      const fields = extractedFields.length > 0 ? extractedFields : extractFieldsFromTerms(newTemplateText);
      const newTemplate: ContractTemplate = {
        id: `custom-${uuidv4()}`,
        name: newTemplateName.trim(),
        category: newTemplateCat.trim() || (folders[0] || 'Custom'),
        description: newTemplateDesc.trim() || 'User uploaded template',
        icon: '📄',
        content: {
          fields,
          terms: newTemplateText
        }
      };

      if (isSupabaseConfigured && supabase) {
        const { error } = await supabase
          .from('contract_templates')
          .insert({
            id: newTemplate.id,
            user_id: currentUser.id,
            name: newTemplate.name,
            category: newTemplate.category,
            description: newTemplate.description,
            icon: newTemplate.icon,
            content: newTemplate.content
          });
        if (error) throw error;
        toast.success('Template uploaded successfully');
      } else {
        saveCustomTemplate(newTemplate);
        toast.success('Template saved locally');
      }

      setShowUploadModal(false);
      setNewTemplateName('');
      setNewTemplateDesc('');
      setNewTemplateCat(folders[0] || 'Custom');
      setNewTemplateFile(null);
      setNewTemplateText('');
      setExtractedFields([]);
      await fetchTemplates();
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const getLeadName = (leadId: string | null) => {
    if (!leadId) return null;
    const lead = leads.find(l => l.id === leadId);
    return lead ? `${lead.name}${lead.propertyAddress ? ` · ${lead.propertyAddress.split(',')[0]}` : ''}` : null;
  };

  const statusCounts = useMemo(() =>
    contracts.reduce((acc, c) => {
      acc[c.status] = (acc[c.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    [contracts]
  );

  const filteredContracts = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return contracts.filter(c => {
      const matchesFilter = activeFilter === 'all' || c.status === activeFilter;
      const leadName = getLeadName(c.lead_id) || '';
      const matchesSearch = !searchQuery ||
        c.title.toLowerCase().includes(searchLower) ||
        leadName.toLowerCase().includes(searchLower);
      return matchesFilter && matchesSearch;
    });
  }, [contracts, activeFilter, searchQuery, leads]);

  const totalPages = Math.ceil(filteredContracts.length / PAGE_SIZE);
  const paginatedContracts = filteredContracts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 on filter/search change
  useEffect(() => { setPage(1); }, [activeFilter, searchQuery]);

  const deletingContract = contracts.find(c => c.id === deletingContractId);

  return (
    <div className="flex flex-col h-full bg-[var(--t-surface)]">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-6 bg-[var(--t-surface)] border-b border-[var(--t-border)] shrink-0">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center">
            <FileSignature className="text-[var(--t-primary)]" size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--t-text)]">Contracts</h1>
            <div className="flex items-center gap-3 mt-0.5 text-xs text-[var(--t-text-muted)]">
              <span>{contracts.length} total</span>
              {Object.entries(statusCounts).filter(([, v]) => v > 0).map(([s, v]) => (
                <span key={s} className="flex items-center gap-0.5">
                  <span className="font-semibold text-[var(--t-text)]">{v}</span> {s}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Page view toggle */}
          <div className="flex items-center bg-[var(--t-surface-hover)] rounded-xl p-1 border border-[var(--t-border)]">
            <button
              onClick={() => setPageView('contracts')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${pageView === 'contracts' ? 'bg-[var(--t-primary)] text-white shadow' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              <FileText size={13} /> Contracts
            </button>
            <button
              onClick={() => setPageView('templates')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${pageView === 'templates' ? 'bg-[var(--t-primary)] text-white shadow' : 'text-[var(--t-text-muted)] hover:text-[var(--t-text)]'}`}
            >
              <BookOpen size={13} /> Templates
            </button>
          </div>

          {pageView === 'templates' && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text)] text-sm font-semibold rounded-xl hover:bg-[var(--t-surface-hover)] hover:border-[var(--t-primary)] transition-all shadow-sm"
            >
              <Upload size={16} className="text-[var(--t-primary)]" />
              Upload Template
            </button>
          )}

          <button
            onClick={() => handleNewContract()}
            disabled={isCreating}
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)] disabled:opacity-60"
          >
            {isCreating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
            New Contract
          </button>
        </div>
      </div>

      {/* ===== CONTRACTS VIEW ===== */}
      {pageView === 'contracts' && (
        <>
          {/* Filter Tabs + Search */}
          <div className="px-8 py-4 border-b border-[var(--t-border)] bg-[var(--t-surface)] shrink-0 flex flex-col gap-3">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              {FILTER_TABS.map(tab => {
                const count = tab.value === 'all' ? contracts.length : (statusCounts[tab.value] || 0);
                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveFilter(tab.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                      activeFilter === tab.value
                        ? 'bg-[var(--t-primary-dim)] text-[var(--t-primary)]'
                        : 'text-[var(--t-text-muted)] hover:bg-[var(--t-surface-hover)]'
                    }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                        activeFilter === tab.value ? 'bg-[var(--t-primary)] text-white' : 'bg-[var(--t-surface-hover)] text-[var(--t-text-muted)]'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--t-text-muted)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title, lead name, or property address..."
                className="w-full pl-9 pr-4 py-2 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)]"
              />
            </div>
          </div>

          {/* Contract List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {isLoading ? (
              <div className="p-6 space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center gap-4 p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-[var(--t-surface-hover)] flex items-center justify-center shrink-0">
                      <div className="w-5 h-5 rounded bg-[var(--t-border)]" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="h-4 bg-[var(--t-border)] rounded w-1/3" />
                      <div className="h-3 bg-[var(--t-border)] rounded w-1/4" />
                    </div>
                    <div className="w-12 h-4 bg-[var(--t-border)] rounded shrink-0" />
                  </div>
                ))}
              </div>
            ) : paginatedContracts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-8">
                <div className="w-16 h-16 rounded-2xl bg-[var(--t-surface)] flex items-center justify-center">
                  <FileSignature size={28} className="text-[var(--t-text-muted)]" />
                </div>
                <div>
                  <p className="font-semibold text-[var(--t-text)] mb-1">
                    {searchQuery || activeFilter !== 'all' ? 'No contracts match your filters' : 'No contracts yet'}
                  </p>
                  <p className="text-sm text-[var(--t-text-muted)]">
                    {activeFilter === 'all' && !searchQuery
                      ? 'Create your first contract or pick a template to get started.'
                      : 'Try changing your filter or search term.'}
                  </p>
                </div>
                {activeFilter === 'all' && !searchQuery && (
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => handleNewContract()} disabled={isCreating} className="flex items-center gap-2 px-5 py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors">
                      <Plus size={16} /> New Contract
                    </button>
                    <button onClick={() => setPageView('templates')} className="flex items-center gap-2 px-5 py-2.5 bg-[var(--t-surface)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)] hover:border-[var(--t-primary)] transition-colors">
                      <BookOpen size={16} /> Browse Templates
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-6 space-y-2">
                {paginatedContracts.map(contract => {
                  const statusCfg = STATUS_CONFIG[contract.status] || STATUS_CONFIG.draft;
                  const StatusIcon = statusCfg.icon;
                  const leadName = getLeadName(contract.lead_id);
                  const buyerSig = contract.signatures?.find(s => s.signer_type === 'buyer');
                  const sellerSig = contract.signatures?.find(s => s.signer_type === 'seller');
                  const canDelete = DELETABLE_STATUSES.includes(contract.status);

                  return (
                    <div
                      key={contract.id}
                      className="flex items-center gap-4 p-4 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-2xl hover:border-[var(--t-primary)] hover:shadow-lg hover:shadow-[var(--t-primary-dim)] transition-all group"
                    >
                      {/* Clickable area */}
                      <div
                        className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer"
                        onClick={() => navigate(`/contracts/${contract.id}`)}
                      >
                        <div className="w-10 h-10 rounded-xl bg-[var(--t-primary-dim)] flex items-center justify-center shrink-0">
                          <FileText size={18} className="text-[var(--t-primary)]" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-[var(--t-text)] truncate">{contract.title}</p>
                            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide shrink-0 ${statusCfg.color}`}>
                              <StatusIcon size={10} />
                              {statusCfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-[var(--t-text-muted)]">
                            {leadName && <span className="truncate max-w-[200px]">📋 {leadName}</span>}
                            <span className="flex items-center gap-1 shrink-0">
                              <Clock size={10} /> {formatDistanceToNow(new Date(contract.updated_at), { addSuffix: true })}
                            </span>
                          </div>
                        </div>

                        {/* Signature indicators */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border ${buyerSig ? 'bg-[var(--t-success-dim)] border-[var(--t-success)] text-[var(--t-success)]' : 'bg-[var(--t-surface-hover)] border-[var(--t-border)] text-[var(--t-text-muted)]'}`} title={buyerSig ? 'Buyer signed' : 'Buyer unsigned'}>B</div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold border ${sellerSig ? 'bg-[var(--t-success-dim)] border-[var(--t-success)] text-[var(--t-success)]' : 'bg-[var(--t-surface-hover)] border-[var(--t-border)] text-[var(--t-text-muted)]'}`} title={sellerSig ? 'Seller signed' : 'Seller unsigned'}>S</div>
                        </div>

                        <ChevronRight size={18} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)] transition-colors shrink-0" />
                      </div>

                      {/* Trash icon */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canDelete) {
                            setDeletingContractId(contract.id);
                            setShowDeleteModal(true);
                          } else {
                            toast.error('Cannot delete active contracts. Only Draft or Rejected contracts can be deleted.');
                          }
                        }}
                        className={`p-2 rounded-xl transition-colors shrink-0 opacity-0 group-hover:opacity-100 ${
                          canDelete
                            ? 'text-[var(--t-text-muted)] hover:text-[var(--t-error)] hover:bg-[var(--t-error)]/10'
                            : 'text-[var(--t-text-muted)] opacity-30 cursor-not-allowed'
                        }`}
                        title={canDelete ? 'Delete contract' : 'Cannot delete active contracts'}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-[var(--t-border)]">
                    <p className="text-xs text-[var(--t-text-muted)]">
                      Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredContracts.length)} of {filteredContracts.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="p-2 rounded-xl bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-text)] disabled:opacity-40 transition-colors"
                      >
                        <ChevronLeft size={15} />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-8 h-8 rounded-xl text-xs font-bold transition-colors ${
                            p === page
                              ? 'bg-[var(--t-primary)] text-white'
                              : 'bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-text)]'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="p-2 rounded-xl bg-[var(--t-surface)] border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-text)] disabled:opacity-40 transition-colors"
                      >
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ===== TEMPLATES VIEW ===== */}
      {pageView === 'templates' && (
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <h2 className="text-lg font-bold text-[var(--t-text)] mb-1">Contract Templates</h2>
              <p className="text-sm text-[var(--t-text-muted)]">
                Choose a template to pre-fill fields and clauses. Your custom templates are saved locally.
              </p>
            </div>

            {/* Folder Management Toolbar */}
            <div className="flex items-center gap-2 mb-6 pb-4 border-b border-[var(--t-border)]">
              <FolderOpen size={15} className="text-[var(--t-primary)]" />
              <span className="text-xs font-bold text-[var(--t-text-muted)] uppercase tracking-wider mr-2">Folders</span>
              {folders.map(folder => (
                <div key={folder} className="group flex items-center gap-1">
                  {editingFolder === folder ? (
                    <div className="flex items-center gap-1">
                      <input
                        autoFocus
                        value={editingFolderName}
                        onChange={e => setEditingFolderName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameFolder(folder);
                          if (e.key === 'Escape') { setEditingFolder(null); setEditingFolderName(''); }
                        }}
                        className="px-2 py-1 text-xs bg-[var(--t-bg)] border border-[var(--t-primary)] rounded-lg text-[var(--t-text)] focus:outline-none w-28"
                      />
                      <button onClick={() => handleRenameFolder(folder)} className="p-1 text-[var(--t-success)] hover:bg-[var(--t-success-dim)] rounded transition-colors" title="Save">
                        <Check size={12} />
                      </button>
                      <button onClick={() => { setEditingFolder(null); setEditingFolderName(''); }} className="p-1 text-[var(--t-text-muted)] hover:bg-[var(--t-surface-hover)] rounded transition-colors" title="Cancel">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-0.5 px-2.5 py-1 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl text-xs text-[var(--t-text-muted)] hover:border-[var(--t-primary)] transition-all">
                      <Folder size={11} className="mr-1 text-[var(--t-primary)]" />
                      <span>{folder}</span>
                      <button
                        onClick={() => { setEditingFolder(folder); setEditingFolderName(folder); }}
                        className="ml-1 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[var(--t-surface-hover)] text-[var(--t-text-muted)] hover:text-[var(--t-text)] transition-all"
                        title="Rename folder"
                      >
                        <Edit2 size={10} />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete folder "${folder}" and all its custom templates?`)) {
                            handleDeleteFolder(folder);
                          }
                        }}
                        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[var(--t-error)]/10 text-[var(--t-text-muted)] hover:text-[var(--t-error)] transition-all"
                        title="Delete folder"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {isAddingFolder ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={newFolderName}
                    onChange={e => setNewFolderName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCreateFolder();
                      if (e.key === 'Escape') { setIsAddingFolder(false); setNewFolderName(''); }
                    }}
                    placeholder="Folder name"
                    className="px-2 py-1 text-xs bg-[var(--t-bg)] border border-[var(--t-primary)] rounded-lg text-[var(--t-text)] focus:outline-none w-28 placeholder:text-[var(--t-text-muted)]"
                  />
                  <button onClick={handleCreateFolder} className="p-1 text-[var(--t-success)] hover:bg-[var(--t-success-dim)] rounded transition-colors" title="Create">
                    <Check size={12} />
                  </button>
                  <button onClick={() => { setIsAddingFolder(false); setNewFolderName(''); }} className="p-1 text-[var(--t-text-muted)] hover:bg-[var(--t-surface-hover)] rounded transition-colors" title="Cancel">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsAddingFolder(true)}
                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-[var(--t-primary)] bg-[var(--t-primary-dim)] rounded-xl hover:bg-[var(--t-primary-dim)] border border-[var(--t-primary)]/30 hover:border-[var(--t-primary)] transition-all"
                >
                  <FolderPlus size={11} /> New Folder
                </button>
              )}
            </div>

            {/* Group by folder/category */}
            {(() => {
              // Show all folders first, then any template categories not in folders
              const allCats = [...new Set(templates.map(t => t.category))];
              const orderedCats = [
                ...folders.filter(f => allCats.includes(f)),
                ...allCats.filter(c => !folders.includes(c))
              ];
              return orderedCats.map(cat => {
                const catTemplates = templates.filter(t => t.category === cat);
                if (catTemplates.length === 0) return null;
                return (
                  <div key={cat} className="mb-8">
                    <h3 className="text-xs font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-3 flex items-center gap-2">
                      <Folder size={12} className="text-[var(--t-primary)]" />
                      <span>{cat}</span>
                      <div className="flex-1 h-px bg-[var(--t-border)]" />
                      <span className="text-[10px] font-normal normal-case tracking-normal text-[var(--t-text-muted)]">
                        {catTemplates.length} template{catTemplates.length !== 1 ? 's' : ''}
                      </span>
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {catTemplates.map(template => {
                        const isCustom = template.id.startsWith('custom-');
                        const isDefault = defaultTemplateId === template.id;
                        return (
                          <div
                            key={template.id}
                            className={`group bg-[var(--t-surface)] border rounded-2xl p-5 hover:shadow-lg hover:shadow-[var(--t-primary-dim)] transition-all flex flex-col ${
                              isDefault
                                ? 'border-[var(--t-primary)] shadow-md shadow-[var(--t-primary-dim)]'
                                : 'border-[var(--t-border)] hover:border-[var(--t-primary)]'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="text-3xl">{template.icon}</div>
                              <div className="flex items-center gap-1">
                                {/* Default star */}
                                <button
                                  onClick={() => handleSetDefaultTemplate(template.id)}
                                  className={`p-1.5 rounded-lg transition-all ${
                                    isDefault
                                      ? 'text-amber-400 bg-amber-400/10'
                                      : 'text-[var(--t-text-muted)] hover:text-amber-400 hover:bg-amber-400/10 opacity-0 group-hover:opacity-100'
                                  }`}
                                  title={isDefault ? 'Default template (click to unset)' : 'Set as default template'}
                                >
                                  <Star size={13} fill={isDefault ? 'currentColor' : 'none'} />
                                </button>
                                {/* Duplicate */}
                                <button
                                  onClick={() => handleDuplicateTemplate(template)}
                                  className="p-1.5 text-[var(--t-text-muted)] hover:text-[var(--t-primary)] hover:bg-[var(--t-primary-dim)] rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                  title="Duplicate template"
                                >
                                  <Copy size={13} />
                                </button>
                                {isCustom && (
                                  <button
                                    onClick={() => handleDeleteTemplate(template.id)}
                                    className="p-1.5 text-[var(--t-text-muted)] hover:text-[var(--t-error)] hover:bg-[var(--t-error)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                    title="Delete template"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {isDefault && (
                              <div className="flex items-center gap-1 mb-2">
                                <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                                  <Star size={8} fill="currentColor" /> Default
                                </span>
                              </div>
                            )}

                            <h4 className="font-bold text-[var(--t-text)] text-sm mb-1">{template.name}</h4>
                            <p className="text-xs text-[var(--t-text-muted)] leading-relaxed flex-1 mb-4">{template.description}</p>

                            <div className="flex items-center gap-2">
                              <div className="flex flex-wrap gap-1 flex-1">
                                {template.content.fields.slice(0, 3).map(f => (
                                  <span key={f.id} className="text-[9px] bg-[var(--t-surface-hover)] text-[var(--t-text-muted)] px-2 py-0.5 rounded-full font-medium">
                                    {f.name}
                                  </span>
                                ))}
                                {template.content.fields.length > 3 && (
                                  <span className="text-[9px] text-[var(--t-text-muted)] px-2 py-0.5">
                                    +{template.content.fields.length - 3} more
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleNewContract(template)}
                                disabled={isCreating}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--t-primary)] text-white text-xs font-bold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shrink-0 shadow shadow-[var(--t-primary-dim)]"
                              >
                                {isCreating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
                                Use
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}

            {templates.length === 0 && (
              <div className="text-center py-16 text-[var(--t-text-muted)]">
                <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
                <p>No templates yet. Upload your first template or create a contract and save it as a template.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setDeletingContractId(null); }} title="Delete Contract" maxWidth="max-w-sm">
        <div className="p-6">
          {deletingContract && DELETABLE_STATUSES.includes(deletingContract.status) ? (
            <>
              <p className="text-[var(--t-text-muted)] text-sm mb-6">
                Are you sure you want to delete <span className="font-bold text-[var(--t-text)]">"{deletingContract.title}"</span>? This cannot be undone.
              </p>
              <div className="flex items-center gap-3">
                <button onClick={() => { setShowDeleteModal(false); setDeletingContractId(null); }} className="flex-1 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)]">Cancel</button>
                <button onClick={handleDeleteContract} disabled={isDeleting} className="flex-1 px-4 py-2.5 bg-[var(--t-error)] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-60">
                  {isDeleting ? <Loader2 size={15} className="animate-spin mx-auto" /> : 'Delete'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col items-center text-center py-4">
                <div className="w-12 h-12 rounded-full bg-[var(--t-warning-dim)] flex items-center justify-center mb-3">
                  <AlertCircle size={22} className="text-[var(--t-warning)]" />
                </div>
                <h3 className="font-bold text-[var(--t-text)] mb-2">Cannot Delete Active Contract</h3>
                <p className="text-sm text-[var(--t-text-muted)]">Only Draft or Rejected contracts can be deleted.</p>
              </div>
              <button onClick={() => { setShowDeleteModal(false); setDeletingContractId(null); }} className="w-full mt-4 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)]">Close</button>
            </>
          )}
        </div>
      </Modal>

      {/* Upload Template Modal */}
      <Modal 
        isOpen={showUploadModal} 
        onClose={() => {
          setShowUploadModal(false);
          setNewTemplateName('');
          setNewTemplateDesc('');
          setNewTemplateCat('Custom');
          setNewTemplateFile(null);
          setNewTemplateText('');
        }} 
        title="Upload Contract Template" 
        maxWidth="max-w-xl"
      >
        <form onSubmit={handleUploadSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] mb-1">
              Template Name *
            </label>
            <input
              type="text"
              required
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="e.g. Standard Lease, NDA, JV Agreement"
              className="w-full px-4 py-2.5 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] mb-1">
                Folder / Category
              </label>
              <select
                value={newTemplateCat}
                onChange={(e) => setNewTemplateCat(e.target.value)}
                className="w-full px-4 py-2.5 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)]"
              >
                {folders.length > 0
                  ? folders.map(f => <option key={f} value={f}>{f}</option>)
                  : <option value="Custom">Custom</option>
                }
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] mb-1">
                Description
              </label>
              <input
                type="text"
                value={newTemplateDesc}
                onChange={(e) => setNewTemplateDesc(e.target.value)}
                placeholder="Brief summary of template use"
                className="w-full px-4 py-2.5 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)] mb-1">
              File Upload (.docx, .pdf, .txt)
            </label>
            <div className="relative border-2 border-dashed border-[var(--t-border)] rounded-2xl p-6 text-center hover:border-[var(--t-primary)] transition-all bg-[var(--t-bg)] flex flex-col items-center justify-center cursor-pointer group">
              <input
                type="file"
                accept=".docx,.pdf,.txt"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Upload size={24} className="text-[var(--t-text-muted)] group-hover:text-[var(--t-primary)] transition-colors mb-2" />
              <p className="text-sm font-semibold text-[var(--t-text)]">
                {newTemplateFile ? newTemplateFile.name : 'Select or drop template file'}
              </p>
              <p className="text-xs text-[var(--t-text-muted)] mt-1">
                Supported formats: PDF, Word (DOCX), or Plain Text (TXT)
              </p>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)]">
                Template Terms / Clauses *
              </label>
              <span className="text-[10px] text-[var(--t-text-muted)]">
                Use <code className="bg-[var(--t-surface-hover)] px-1 rounded">{"{{field_name}}"}</code> to insert dynamic fields
              </span>
            </div>
            <textarea
              required
              rows={8}
              value={newTemplateText}
              onChange={(e) => setNewTemplateText(e.target.value)}
              placeholder="Paste or edit the contract terms here. E.g. This agreement is between {{buyer_name}} and {{seller_name}}..."
              className="w-full px-4 py-2.5 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-xl text-sm text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] placeholder:text-[var(--t-text-muted)] custom-scrollbar font-mono leading-relaxed"
            />
          </div>

          {/* Field Customization Grid */}
          {extractedFields.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-[var(--t-text-muted)]">
                  Detected Fields ({extractedFields.length})
                </label>
                <span className="text-[10px] text-[var(--t-text-muted)]">Customize type & visibility</span>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                {extractedFields.map((field, idx) => (
                  <div
                    key={field.id}
                    className="flex items-center gap-2 p-2.5 bg-[var(--t-surface)] border border-[var(--t-border)] rounded-xl"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[var(--t-text)] truncate">{field.name}</p>
                      <p className="text-[10px] text-[var(--t-text-muted)] font-mono">{`{{${field.id}}}`}</p>
                    </div>
                    <select
                      value={field.type}
                      onChange={e => setExtractedFields(prev => prev.map((f, i) =>
                        i === idx ? { ...f, type: e.target.value as ContractField['type'] } : f
                      ))}
                      className="px-2 py-1 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-lg text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] shrink-0"
                    >
                      <option value="text">Text</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                      <option value="currency">Currency</option>
                      <option value="textarea">Paragraph</option>
                    </select>
                    <select
                      value={field.visibility}
                      onChange={e => setExtractedFields(prev => prev.map((f, i) =>
                        i === idx ? { ...f, visibility: e.target.value as ContractField['visibility'] } : f
                      ))}
                      className="px-2 py-1 bg-[var(--t-bg)] border border-[var(--t-border)] rounded-lg text-xs text-[var(--t-text)] focus:outline-none focus:border-[var(--t-primary)] shrink-0"
                    >
                      <option value="all">All parties</option>
                      <option value="agent_only">Agent only</option>
                      <option value="buyer_only">Buyer only</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowUploadModal(false);
                setNewTemplateName('');
                setNewTemplateDesc('');
                setNewTemplateCat('Custom');
                setNewTemplateFile(null);
                setNewTemplateText('');
              }}
              className="flex-1 px-4 py-2.5 bg-[var(--t-surface-hover)] text-[var(--t-text)] text-sm font-semibold rounded-xl border border-[var(--t-border)]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 px-4 py-2.5 bg-[var(--t-primary)] text-white text-sm font-semibold rounded-xl hover:bg-[var(--t-primary-hover)] transition-colors shadow-lg shadow-[var(--t-primary-dim)] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Processing...
                </>
              ) : (
                'Upload Template'
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
